/**
 * BulkSalaryReviewModalV2 - Bulk salary update wizard with Tailwind/shadcn UI.
 * Replaces ChakraUi/Modals/BulkSalaryReviewModal.tsx
 */
import React, { useState, useRef } from 'react';
import {
	Download,
	Upload,
	Loader2,
	CheckCircle2,
	AlertCircle,
	ArrowLeft,
	Check,
	Edit2
} from 'lucide-react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useToast } from '../ui/use-toast';
import bulkUpdateSalaries from '../../api/users/bulkUpdateSalaries';
import downloadSalaryTemplate from '../../api/users/downloadSalaryTemplate';
import previewSalaryChanges, {
	SalaryChangePreview,
	SalaryPreviewError
} from '../../api/users/previewSalaryChanges';
import { formatArcaNumber } from '../../utils/formatArcaNumbers';
import { analytics } from '../../services/analytics';
import { descargarFichero } from '../../movil/descargas';
import { cn } from '../../lib/utils';

type WizardStep = 'intro' | 'preview' | 'success';

const formatSalary = (salary: number | null, translate: (key: string) => string): string => {
	if (salary === null) return translate('admin.bulkSalary.noSalary');
	return `${formatArcaNumber(salary)} €`;
};

interface BulkSalaryReviewModalV2Props {
	isOpen: boolean;
	onClose: () => void;
	token: string;
	companyId: string;
	userId: string;
	onSuccess: () => void;
	translate: (key: string) => string;
}

const BulkSalaryReviewModalV2: React.FC<BulkSalaryReviewModalV2Props> = ({
	isOpen,
	onClose,
	token,
	companyId,
	userId,
	onSuccess,
	translate
}) => {
	const { toast } = useToast();
	const [step, setStep] = useState<WizardStep>('intro');
	const [isDownloading, setIsDownloading] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [isConfirming, setIsConfirming] = useState(false);
	const [changes, setChanges] = useState<SalaryChangePreview[]>([]);
	const [errors, setErrors] = useState<SalaryPreviewError[]>([]);
	const [result, setResult] = useState<{ successCount: number; failedCount: number } | null>(null);
	const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
	const [isDragOver, setIsDragOver] = useState(false);
	const [editedSalaries, setEditedSalaries] = useState<Record<string, number | null>>({});
	const [editInputValues, setEditInputValues] = useState<Record<string, string>>({});
	const [editingRows, setEditingRows] = useState<Set<string>>(new Set());
	const fileInputRef = useRef<HTMLInputElement>(null);

	const reviewUsers = changes.filter((c) => c.needsReview && !(c.userId in editedSalaries));
	const confirmedReviewUsers = changes.filter((c) => c.needsReview && c.userId in editedSalaries);
	const changedUsers = changes.filter((c) => c.hasChange && !c.needsReview);
	const unchangedUsers = changes.filter((c) => !c.hasChange && !c.needsReview);
	const totalChangesToApply = changedUsers.length + confirmedReviewUsers.length;

	const handleDownloadTemplate = async () => {
		analytics.track('bulk_salary_funnel', { step: 'download_template' });
		setIsDownloading(true);
		try {
			const response = await downloadSalaryTemplate(token, companyId);
			await descargarFichero({
				nombre: response.filename,
				base64: response.csvBase64,
				tipo: 'text/csv;charset=utf-8',
				comoTexto: true
			});
		} catch (error) {
			toast({ title: translate('admin.bulkSalary.downloadError'), variant: 'destructive' });
		} finally {
			setIsDownloading(false);
		}
	};

	const processFile = async (file: File) => {
		analytics.track('bulk_salary_funnel', { step: 'file_upload' });
		if (!file.name.toLowerCase().endsWith('.csv')) {
			toast({
				title: translate('admin.bulkSalary.uploadError'),
				description: translate('admin.bulkSalary.invalidFileType'),
				variant: 'destructive'
			});
			return;
		}

		setSelectedFileName(file.name);
		setIsUploading(true);

		try {
			const reader = new FileReader();
			reader.onload = async (e) => {
				const result = e.target?.result as string;
				const base64Content = result.includes(',') ? result.split(',')[1] : result;

				try {
					const response = await previewSalaryChanges(token, companyId, base64Content);
					setChanges(response.changes);
					setErrors(response.errors);
					setStep('preview');
				} catch (error) {
					toast({ title: translate('admin.bulkSalary.uploadError'), variant: 'destructive' });
				} finally {
					setIsUploading(false);
				}
			};
			reader.readAsDataURL(file);
		} catch (error) {
			setIsUploading(false);
			toast({ title: translate('admin.bulkSalary.uploadError'), variant: 'destructive' });
		}
	};

	const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;
		await processFile(file);
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setIsDragOver(true);
	};

	const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setIsDragOver(false);
	};

	const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setIsDragOver(false);
		const file = event.dataTransfer.files?.[0];
		if (file) await processFile(file);
	};

	const handleStartEditing = (userId: string, currentValue: number | null) => {
		setEditingRows((prev) => new Set(prev).add(userId));
		setEditInputValues((prev) => ({ ...prev, [userId]: currentValue?.toString() || '' }));
	};

	const handleConfirmReviewItem = (userId: string) => {
		const inputValue = editInputValues[userId];
		if (inputValue === undefined || inputValue.trim() === '') return;
		const parsed = parseFloat(inputValue.replace(/[^\d.]/g, ''));
		if (!isNaN(parsed) && parsed >= 0) {
			setEditedSalaries((prev) => ({ ...prev, [userId]: parsed }));
			setEditingRows((prev) => {
				const next = new Set(prev);
				next.delete(userId);
				return next;
			});
		}
	};

	const handleConfirm = async () => {
		if (totalChangesToApply === 0) return;
		analytics.track('bulk_salary_funnel', { step: 'confirm_updates' });
		setIsConfirming(true);
		try {
			const autoChanges = changedUsers.map((c) => ({
				userId: c.userId,
				newSalary: c.userId in editedSalaries ? editedSalaries[c.userId] : c.newSalary
			}));
			const manualChanges = confirmedReviewUsers.map((c) => ({
				userId: c.userId,
				newSalary: editedSalaries[c.userId]
			}));
			const allChanges = [...autoChanges, ...manualChanges];

			const response = await bulkUpdateSalaries(token, companyId, allChanges);
			setResult({ successCount: response.successCount, failedCount: response.failedCount });
			setStep('success');
			if (response.successCount > 0) onSuccess();
		} catch (error) {
			toast({ title: translate('admin.bulkSalary.updateError'), variant: 'destructive' });
		} finally {
			setIsConfirming(false);
		}
	};

	const handleClose = () => {
		setStep('intro');
		setChanges([]);
		setErrors([]);
		setResult(null);
		setSelectedFileName(null);
		setEditedSalaries({});
		setEditInputValues({});
		setEditingRows(new Set());
		onClose();
	};

	const handleBackToIntro = () => {
		setStep('intro');
		setChanges([]);
		setErrors([]);
		setSelectedFileName(null);
		setEditedSalaries({});
		setEditInputValues({});
		setEditingRows(new Set());
	};

	const renderStepIndicator = () => {
		const currentIndex = step === 'success' ? 1 : step === 'preview' ? 1 : 0;
		return (
			<div className="flex items-center justify-center gap-2 mb-6">
				<div
					className={cn(
						'w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold',
						currentIndex >= 0 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
					)}
				>
					1
				</div>
				<div className={cn('w-10 h-0.5', currentIndex >= 1 ? 'bg-primary' : 'bg-muted')} />
				<div
					className={cn(
						'w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold',
						currentIndex >= 1 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
					)}
				>
					2
				</div>
			</div>
		);
	};

	const renderIntroStep = () => (
		<div className="space-y-6">
			<div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
				<p className="text-sm text-blue-800">
					{translate('admin.bulkSalary.downloadInfoMessage1')}
					<span className="font-bold">{translate('admin.bulkSalary.downloadInfoMessage2')}</span>
					{translate('admin.bulkSalary.downloadInfoMessage3')}
				</p>
			</div>

			<div className="space-y-4">
				<div className="rounded-lg border border-border bg-muted/30 p-4 text-center">
					<p className="font-medium mb-3">{translate('admin.bulkSalary.step1Title')}</p>
					<Button variant="outline" onClick={handleDownloadTemplate} disabled={isDownloading}>
						{isDownloading ? (
							<Loader2 className="w-4 h-4 mr-2 animate-spin" />
						) : (
							<Download className="w-4 h-4 mr-2" />
						)}
						{translate('admin.bulkSalary.downloadButton')}
					</Button>
				</div>

				<div
					className={cn(
						'rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-all',
						isDragOver
							? 'border-primary bg-primary/5'
							: selectedFileName
								? 'border-primary bg-primary/5'
								: 'border-border hover:border-primary/50 hover:bg-muted/30'
					)}
					onDragOver={handleDragOver}
					onDragEnter={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					onClick={() => !isUploading && fileInputRef.current?.click()}
				>
					<p className="font-medium mb-3">{translate('admin.bulkSalary.step2Title')}</p>
					<input
						ref={fileInputRef}
						type="file"
						accept=".csv"
						onChange={handleFileSelect}
						className="hidden"
					/>
					{isUploading ? (
						<div className="flex items-center justify-center gap-2">
							<Loader2 className="w-5 h-5 animate-spin text-primary" />
							<span className="text-primary">{translate('admin.bulkSalary.processing')}</span>
						</div>
					) : (
						<div className="space-y-2">
							<Upload
								className={cn(
									'w-8 h-8 mx-auto',
									isDragOver ? 'text-primary' : 'text-muted-foreground'
								)}
							/>
							<p className="text-sm text-muted-foreground">
								{translate('admin.bulkSalary.dragOrClick')}
							</p>
							<Button
								size="sm"
								variant="outline"
								onClick={(e) => {
									e.stopPropagation();
									fileInputRef.current?.click();
								}}
							>
								<Upload className="w-4 h-4 mr-2" />
								{translate('admin.bulkSalary.attachFile')}
							</Button>
						</div>
					)}
					{selectedFileName && (
						<p className="text-sm text-primary mt-2 font-medium">{selectedFileName}</p>
					)}
				</div>
			</div>
		</div>
	);

	const renderPreviewStep = () => (
		<div className="space-y-4">
			<div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
				<div className="text-center">
					<p className="text-xs text-muted-foreground">
						{translate('admin.bulkSalary.totalEmployees')}
					</p>
					<p className="text-xl font-bold">{changes.length}</p>
				</div>
				<div className="text-center">
					<p className="text-xs text-primary">{translate('admin.bulkSalary.withChanges')}</p>
					<p className="text-xl font-bold text-primary">
						{changedUsers.length + confirmedReviewUsers.length}
					</p>
				</div>
				{reviewUsers.length > 0 && (
					<div className="text-center">
						<p className="text-xs text-amber-600">{translate('admin.bulkSalary.needsReview')}</p>
						<p className="text-xl font-bold text-amber-600">{reviewUsers.length}</p>
					</div>
				)}
				<div className="text-center">
					<p className="text-xs text-muted-foreground">{translate('admin.bulkSalary.noChanges')}</p>
					<p className="text-xl font-bold text-muted-foreground">{unchangedUsers.length}</p>
				</div>
			</div>

			{errors.length > 0 && (
				<div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
					<p className="font-medium text-destructive mb-2 flex items-center gap-2">
						<AlertCircle className="w-4 h-4" />
						{translate('admin.bulkSalary.errorsLabel')} ({errors.length})
					</p>
					<ul className="text-sm text-destructive space-y-1">
						{errors.slice(0, 5).map((err, i) => (
							<li key={i}>
								• {err.nif}: {err.message}
							</li>
						))}
						{errors.length > 5 && <li>... +{errors.length - 5} más</li>}
					</ul>
				</div>
			)}

			<ScrollArea className="h-[300px] rounded-lg border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{translate('admin.bulkSalary.employee')}</TableHead>
							<TableHead className="text-right">
								{translate('admin.bulkSalary.currentSalary')}
							</TableHead>
							<TableHead className="text-right">
								{translate('admin.bulkSalary.newSalary')}
							</TableHead>
							<TableHead>{translate('admin.bulkSalary.status')}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{changedUsers.map((change) => (
							<TableRow key={change.userId}>
								<TableCell className="font-medium">{change.email}</TableCell>
								<TableCell className="text-right">
									{formatSalary(change.currentSalary, translate)}
								</TableCell>
								<TableCell className="text-right text-primary font-medium">
									{formatSalary(change.newSalary, translate)}
								</TableCell>
								<TableCell>
									<Badge className="bg-primary/10 text-primary">
										{translate('admin.bulkSalary.changed')}
									</Badge>
								</TableCell>
							</TableRow>
						))}
						{reviewUsers.map((change) => (
							<TableRow key={change.userId} className="bg-amber-50/50">
								<TableCell className="font-medium">{change.email}</TableCell>
								<TableCell className="text-right">
									{formatSalary(change.currentSalary, translate)}
								</TableCell>
								<TableCell className="text-right">
									{editingRows.has(change.userId) ? (
										<div className="flex items-center gap-2 justify-end">
											<Input
												className="w-24 h-8 text-right"
												value={editInputValues[change.userId] || ''}
												onChange={(e) =>
													setEditInputValues((prev) => ({
														...prev,
														[change.userId]: e.target.value
													}))
												}
												placeholder="0"
											/>
											<Button
												size="sm"
												variant="ghost"
												onClick={() => handleConfirmReviewItem(change.userId)}
											>
												<Check className="w-4 h-4" />
											</Button>
										</div>
									) : (
										<Button
											size="sm"
											variant="ghost"
											onClick={() => handleStartEditing(change.userId, change.currentSalary)}
										>
											<Edit2 className="w-4 h-4 mr-1" />
											{translate('admin.bulkSalary.enterSalary')}
										</Button>
									)}
								</TableCell>
								<TableCell>
									<Badge variant="outline" className="border-amber-500 text-amber-600">
										{translate('admin.bulkSalary.needsReview')}
									</Badge>
								</TableCell>
							</TableRow>
						))}
						{confirmedReviewUsers.map((change) => (
							<TableRow key={change.userId} className="bg-primary/5">
								<TableCell className="font-medium">{change.email}</TableCell>
								<TableCell className="text-right">
									{formatSalary(change.currentSalary, translate)}
								</TableCell>
								<TableCell className="text-right text-primary font-medium">
									{formatArcaNumber(editedSalaries[change.userId]!)} €
								</TableCell>
								<TableCell>
									<Badge className="bg-primary/10 text-primary">
										{translate('admin.bulkSalary.confirmed')}
									</Badge>
								</TableCell>
							</TableRow>
						))}
						{unchangedUsers.map((change) => (
							<TableRow key={change.userId} className="text-muted-foreground">
								<TableCell>{change.email}</TableCell>
								<TableCell className="text-right">
									{formatSalary(change.currentSalary, translate)}
								</TableCell>
								<TableCell className="text-right">—</TableCell>
								<TableCell>
									<Badge variant="secondary">{translate('admin.bulkSalary.unchanged')}</Badge>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</ScrollArea>
		</div>
	);

	const renderSuccessStep = () => (
		<div className="text-center py-8 space-y-4">
			<CheckCircle2 className="w-16 h-16 text-primary mx-auto" />
			<div>
				<p className="text-lg font-medium">{translate('admin.bulkSalary.updateComplete')}</p>
				<p className="text-muted-foreground">
					{result?.successCount} {translate('admin.bulkSalary.updated')}
					{result?.failedCount
						? `, ${result.failedCount} ${translate('admin.bulkSalary.failed')}`
						: ''}
				</p>
			</div>
		</div>
	);

	const getTitle = () => {
		if (step === 'intro') return translate('admin.bulkSalary.downloadInfoTitle');
		if (step === 'preview')
			return (
				<div className="flex items-center gap-2">
					<span>{translate('admin.bulkSalary.reviewTitle')}</span>
					{totalChangesToApply > 0 && (
						<Badge className="bg-primary text-white">
							{totalChangesToApply} {translate('admin.bulkSalary.changesToApply')}
						</Badge>
					)}
				</div>
			);
		return translate('admin.bulkSalary.updateComplete');
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col">
				<DialogHeader>
					<DialogTitle>{getTitle()}</DialogTitle>
					<DialogDescription>{translate('admin.bulkSalary.description')}</DialogDescription>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto py-4">
					{renderStepIndicator()}
					{step === 'intro' && renderIntroStep()}
					{step === 'preview' && renderPreviewStep()}
					{step === 'success' && renderSuccessStep()}
				</div>

				<DialogFooter className="gap-2 pt-4 border-t">
					{step === 'intro' && (
						<Button variant="outline" onClick={handleClose}>
							{translate('general.genericModal.close')}
						</Button>
					)}
					{step === 'preview' && (
						<>
							<Button variant="outline" onClick={handleBackToIntro}>
								<ArrowLeft className="w-4 h-4 mr-2" />
								{translate('admin.bulkSalary.back')}
							</Button>
							<Button
								onClick={handleConfirm}
								disabled={totalChangesToApply === 0 || isConfirming}
								className="bg-primary hover:bg-primary/90"
							>
								{isConfirming && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
								{isConfirming
									? translate('admin.bulkSalary.updating')
									: `${translate('admin.bulkSalary.confirm')} (${totalChangesToApply})`}
							</Button>
						</>
					)}
					{step === 'success' && (
						<Button onClick={handleClose} className="bg-primary hover:bg-primary/90">
							{translate('admin.bulkSalary.close')}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default BulkSalaryReviewModalV2;
